import React, { useCallback } from "react";

/**
 * =========================
 * Utils
 * =========================
 */

const normalizeNFD = (s) => String(s ?? "").normalize("NFD");

const fetchTextOrThrow = async (url) => {
  const res = await fetch(url, {});
  if (!res.ok) throw new Error(`fetch failed: ${res.status} ${url}`);
  return await res.text();
};

const stripTrailingNewlines = (text) => {
  // 元コードの「末尾が \r\n や \n のとき削る」を安全に
  return String(text ?? "").replace(/\r?\n$/, "").replace(/\r?\n$/, "");
};

const parseCsvRows = (text) => {
  // 元コードに合わせて「\n split → , split」(CRは除去)
  return stripTrailingNewlines(text)
    .split("\n")
    .map((line) => line.replace("\r", ""))
    .map((line) => line.split(","));
};

const fetchCsvRows = async ({ no, year, area, csvFilename }) => {
  const a = normalizeNFD(area);
  const c = normalizeNFD(csvFilename);
  const filename = `/csv/${no}/${a}_${c}_${year}.csv`;
  const text = await fetchTextOrThrow(filename);
  return parseCsvRows(text);
};

const toNumber = (v) => Number(v);

/**
 * =========================
 * Years builder (cache)
 * =========================
 * - 元の years グローバルを廃止
 * - meta.no + years構成でキー化してキャッシュ
 */
const yearsCache = new Map();

const buildYearsKey = (meta) => {
  // year1..year6 と no と特例(no==07-01)をキーに
  const ys = ["year1", "year2", "year3", "year4", "year5", "year6"]
    .map((k) => (meta?.[k] ? String(meta[k]) : ""))
    .join("|");
  return `${meta?.no ?? ""}__${ys}`;
};

function makeYears(meta) {
  const key = buildYearsKey(meta);
  if (yearsCache.has(key)) return yearsCache.get(key);

  let yearstmp = [];
  let yearsbeginendtmp = [];

  if (String(meta?.no) === "07-01") {
    yearstmp = ["1月", "4月", "7月", "10月", "1月"];
    yearsbeginendtmp = [
      ["2000-01-01", "2000-04-01"],
      ["2000-04-01", "2000-07-01"],
      ["2000-07-01", "2000-10-01"],
      ["2000-10-01", "2000-12-31"],
    ];
  } else {
    const yearslist = ["year1", "year2", "year3", "year4", "year5", "year6"];
    for (const k of yearslist) {
      if (meta?.[k]) yearstmp.push(meta[k]);
    }

    const firstyear = yearstmp[0];
    const bflastyear = yearstmp.slice(-2)[0];
    const lastyear = yearstmp.slice(-1)[0];

    let lastyearend;
    if (firstyear === lastyear) {
      // 1時点
      lastyearend = toNumber(lastyear) + 20;
    } else if (firstyear === bflastyear) {
      // 2時点
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(firstyear));
    } else {
      // 3時点以上
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(bflastyear));
    }

    for (let i = 0; i < yearstmp.length; i++) {
      const begin = yearstmp[i];
      const end = yearstmp[i + 1] ?? lastyearend;
      yearsbeginendtmp.push([begin, end]);
    }
  }

  const result = [yearstmp, yearsbeginendtmp];
  yearsCache.set(key, result);
  return result;
}

/**
 * =========================
 * KML building shared parts
 * =========================
 */

const pickColorIndex = (c, shikii1, shikii2, shikii3, shikii4) => {
  const v = toNumber(c);
  const s1 = toNumber(shikii1);
  const s2 = toNumber(shikii2);
  const s3 = toNumber(shikii3);
  const s4 = toNumber(shikii4);

  if (v >= s1) return "1";
  if (v >= s2) return "2";
  if (v >= s3) return "3";
  if (v >= s4) return "4";
  return "5";
};

const buildPlacemarkKml = ({
  meshcode,
  h,
  c,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  lon,
  lat,
  lon2,
  lat2,
  latc,
  lonc,
  hdis,
}) => {
  const colorkml1 = pickColorIndex(c, shikii1, shikii2, shikii3, shikii4);

  return `
  <Placemark>
  <name>メッシュ番号：${meshcode}</name>
  <description>${height_name}：${h}</description>
  <styleUrl>#PolyCol${colorkml1}</styleUrl>
  <Polygon>
  <altitudeMode>relativeToGround</altitudeMode>
  <extrude>1</extrude>
  <outerBoundaryIs>
  <LinearRing>
  <coordinates>${lon},${lat},${hdis}
  ${lon2},${lat},${hdis}
  ${lon2},${lat2},${hdis}
  ${lon},${lat2},${hdis}
  ${lon},${lat},${hdis}
  </coordinates>
  </LinearRing>
  </outerBoundaryIs>
  </Polygon>
  <LookAt>
  <latitude>${latc}</latitude>
  <longitude>${lonc}</longitude>
  <altitude>0</altitude>
  <range>1000</range>
  <tilt>0</tilt>
  <heading>0</heading>
  <altitudeMode>relativeToGround</altitudeMode>
  </LookAt>
  </Placemark>
  `;
};

/**
 * =========================
 * Mesh → KML (2nd / 3rd / 4th)
 * =========================
 */

function mesh2nd2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;
  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh3rd2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;
  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh3rd2kml3_nihon(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat_p = p / 1.5 + q / 12 + r / 120;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh4th2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = lat + 0.008333333 / 4;
  const lonc = lon + 0.0125 / 4;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh4th2kml3_nihon(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat_p = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = lat + 0.008333333 / 4;
  const lonc = lon + 0.0125 / 4;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

/**
 * =========================
 * getkml2 (csv → per-row kml)
 * =========================
 *
 * - (4) 対策: center計算でCSVを二度読まないよう、rows も返す版
 */
async function getkml2(no, year, area, csvFilename, meta, ratio) {
  const rows = await fetchCsvRows({ no, year, area, csvFilename });

  // 元コード: r[0][1] がないと何もしない
  if (!rows?.[0]?.[1]) return { kml: "", rows, meshLen: 0 };

  const meshLen = String(rows[0][0] ?? "").length;

  // mesh種類ごとの「min」「ratio」「しきい」「関数」を束ねる
  const meshSpecByLen = {
    6: {
      min: () => toNumber(meta.kokudo_min),
      ratio: () => meta.kokudo_ratio,
      // (6) 対策: kokudo_shikii4 が無い場合は shikii3 を採用（旧挙動互換）
      shikii: () => [
        meta.kokudo_shikii1,
        meta.kokudo_shikii2,
        meta.kokudo_shikii3,
        meta.kokudo_shikii4 ?? meta.kokudo_shikii3,
      ],
      kml: mesh2nd2kml3,
      heightName: () => `${meta.hanrei_height_name}${meta.hanrei_height_name_2}`,
    },
    8: {
      min: () => toNumber(meta.min),
      ratio: () => meta.ratio,
      shikii: () => [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      kml: mesh3rd2kml3,
      heightName: () => `${meta.hanrei_height_name}${meta.hanrei_height_name_2}`,
    },
    9: {
      min: () => toNumber(meta.min),
      ratio: () => meta.ratio,
      shikii: () => [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      kml: mesh4th2kml3,
      heightName: () => `${meta.hanrei_height_name}${meta.hanrei_height_name_2}`,
    },
  };

  const spec = meshSpecByLen[meshLen];
  if (!spec) return { kml: "", rows, meshLen };

  const minValue = spec.min();
  const ratioValue = spec.ratio();
  const [s1, s2, s3, s4] = spec.shikii();
  const heightName = spec.heightName();
  const fn = spec.kml;

  let tmp = "";
  for (let i = 0; i < rows.length; i++) {
    const mesh = rows[i][0];
    const h = rows[i][1];
    const c = rows[i][2];

    if (toNumber(h) > minValue) {
      tmp += fn(mesh, h, c, ratioValue, s1, s2, s3, s4, heightName, ratio);
    }
  }
  return { kml: tmp, rows, meshLen };
}

/**
 * =========================
 * KML years folder + overlay
 * =========================
 */

function kml_years_pre2(no, data999kml, yearsbeginend, yearsCount) {
  let kmlkml = "";
  const siteurl = window.location.origin;

  yearsbeginend.forEach((years, i) => {
    if (!data999kml?.[i]) return;

    const folderName = years[0];

    const enableTimeSpan = Number(yearsCount) >= 2;
    const hasBeginEnd = years && years[0] != null && years[1] != null;
    const timeSpan =
      enableTimeSpan && hasBeginEnd
        ? `<TimeSpan><begin>${years[0]}</begin><end>${years[1]}</end></TimeSpan>`
        : "";

    const overlayTimeSpan =
      enableTimeSpan && hasBeginEnd
        ? `<TimeSpan><begin>${years[0]}</begin><end>${years[1]}</end></TimeSpan>`
        : "";

    kmlkml +=
      `<Folder><name>${folderName}</name>` +
      timeSpan +
      `
          <ScreenOverlay>
          <name>凡例表示</name><visibility>1</visibility>
          ${overlayTimeSpan}
          <Icon><href>${siteurl}/hanrei/hanrei_3rdmesh/${no}_${years[0]}.png</href></Icon>
          <overlayXY x="0" y="1" xunits="fraction" yunits="fraction"/>
          <screenXY x="0" y="1" xunits="fraction" yunits="fraction"/>
          <rotationXY x="0" y="0" xunits="fraction" yunits="fraction"/>
          <size x="0" y="0" xunits="fraction" yunits="fraction"/>
          </ScreenOverlay>
          ${data999kml[i]}
          </Folder>
          `;
  });

  return kmlkml;
}

function makekml_years(meta, data999kml, yearbeginend, yearsCount) {
  return kml_years_pre2(meta.no, data999kml, yearbeginend, yearsCount);
}

/**
 * =========================
 * makekml (styles + lookAt)
 * =========================
 */

function makekml(kml_years, viewarray, meta, area, alpha) {
  const kml1_colora = meta.kml1_color;
  const kml2_colora = meta.kml2_color;
  const kml3_colora = meta.kml3_color;
  const kml4_colora = meta.kml4_color;
  const kml5_colora = meta.kml5_color;

  // alpha → 00/33/66/c8/ff
  const alphaset = [0.01, 0.2, 0.4, 0.9, 1];
  const alphano = alphaset.findIndex((tmp) => tmp >= alpha);
  const alpha16css = ["00", "33", "66", "c8", "ff"];
  const alphavalue = alpha16css[alphano] || "c8";

  const toKmlColor = (cssHex) => {
    if (!cssHex) return null;
    // "#RRGGBB" → "AABBGGRR"
    return (
      alphavalue +
      cssHex.slice(5, 7) +
      cssHex.slice(3, 5) +
      cssHex.slice(1, 3)
    );
  };

  const kml1_color = toKmlColor(kml1_colora);
  const kml2_color = toKmlColor(kml2_colora);
  const kml3_color = toKmlColor(kml3_colora);
  const kml4_color = toKmlColor(kml4_colora);
  const kml5_color = toKmlColor(kml5_colora);

  const title = meta.title;
  const cityname = area;

  const [latcenter, loncenter, lonheightratio] = viewarray;

  return Promise.resolve(
    `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${cityname + "_" + title}</name>

    <!-- (1) 対策: 1/2 も null ならフォールバック -->
    <Style id="PolyCol1"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml1_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol2"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml2_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol3"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml3_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol4"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml4_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol5"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml5_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>

    <LookAt>
      <longitude>${loncenter}</longitude>
      <latitude>${latcenter}</latitude>
      <altitude>0</altitude>
      <range>${lonheightratio}</range>
      <tilt>30</tilt>
      <heading>-25</heading>
      <altitudeMode>absolute</altitudeMode>
    </LookAt>

    ${kml_years}

  </Document>
</kml>`
  );
}

/**
 * =========================
 * Center calculation
 * =========================
 */

function mesh2ndcenter(meshcode2nd) {
  const str = String(meshcode2nd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;

  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh3rdcenter(meshcode3rd) {
  const str = String(meshcode3rd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh4thcenter(meshcode4th) {
  const str = String(meshcode4th);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

/**
 * (2) 対策: meshLen に応じて center 計算を分岐
 * (4) 対策: rows を使い回して CSV の再fetchを回避
 */
function calcCenterFromRows(rows, meshLen, minValue) {
  if (!rows || rows.length === 0) return [0, 0, 100000];

  const pickCenterFn =
    meshLen === 6 ? mesh2ndcenter : meshLen === 9 ? mesh4thcenter : mesh3rdcenter;

  let latmax = -Infinity;
  let latmin = Infinity;
  let lonmax = -Infinity;
  let lonmin = Infinity;

  for (let i = 0; i < rows.length; i++) {
    if (toNumber(rows[i][1]) > toNumber(minValue)) {
      const [lat, lon] = pickCenterFn(rows[i][0]);

      if (latmax < lat) latmax = lat;
      if (lonmax < lon) lonmax = lon;
      if (latmin > lat) latmin = lat;
      if (lonmin > lon) lonmin = lon;
    }
  }

  // 対象セルが無いとき
  if (!isFinite(latmax) || !isFinite(latmin) || !isFinite(lonmax) || !isFinite(lonmin)) {
    return [0, 0, 100000];
  }

  const latcenter = (latmax + latmin) / 2;
  const loncenter = (lonmax + lonmin) / 2;

  let lonheightratio = 170000 * (lonmax - lonmin);
  if (lonmax <= lonmin) lonheightratio = 100000;

  return [latcenter, loncenter, lonheightratio];
}

/**
 * =========================
 * Component
 * =========================
 */

const Csv2kml = ({ meta, area, ratio, alpha, children }) => {
  const handlerClickDownloadButton = useCallback(
    async (e) => {
      e.preventDefault();

      const [years, yearbeginend] = makeYears(meta);

      // years分のKML + rows もまとめて取得（(4) 二度fetch回避）
      const yearResults = await Promise.all(
        years.map((year) => getkml2(meta.no, year, area, meta.csv_filename, meta, ratio))
      );

      const data999kml2 = yearResults.map((r) => r.kml);

      // center 用：従来互換で「meta.year5」を優先し、無ければ最後の年を使う
      const centerYear =
        years.find((y) => String(y) === String(meta.year5)) ?? years[years.length - 1];

      const centerResult =
        yearResults.find((r, idx) => String(years[idx]) === String(centerYear)) ??
        yearResults[yearResults.length - 1];

      const centerMin =
        centerResult.meshLen === 6 ? toNumber(meta.kokudo_min) : toNumber(meta.min);

      const kmlcenter = calcCenterFromRows(centerResult.rows, centerResult.meshLen, centerMin);

      const kml_years = makekml_years(meta, data999kml2, yearbeginend, years.length);
      const kmlll = await makekml(kml_years, kmlcenter, meta, area, alpha);

      const blob = new Blob([kmlll], {
        type: "application/vnd.google-earth.kml+xml",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.download = `${area}_${meta.csv_filename}.kml`;
      a.href = url;
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    [meta, area, ratio, alpha]
  );

  return <button onClick={handlerClickDownloadButton}>{children}</button>;
};

export default Csv2kml;